import React from 'react';
import PropTypes from 'prop-types';
import { Table, Input, InputNumber, Form } from 'antd';
import isString from 'lodash/isString';
import get from 'lodash/get';
import Button from './Button';

const EditableContext = React.createContext();

class EditableCell extends React.Component {
  getInput = () => {
    if (this.props.inputType === 'number') {
      return <InputNumber />;
    }
    return <Input />;
  };

  renderCell = ({ getFieldDecorator }) => {
    const { editing, dataIndex, render, title, inputType, record, index, children, ...restProps } = this.props;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item style={{ margin: 0 }}>
            {getFieldDecorator(dataIndex, {
              rules: [
                {
                  required: false,
                  message: `Please Input ${title}!`,
                },
              ],
              initialValue: (render && render(null, record)) || record[dataIndex],
            })(this.getInput())}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  render() {
    return <EditableContext.Consumer>{this.renderCell}</EditableContext.Consumer>;
  }
}

class EditableTable extends React.Component {
  static propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    actions: PropTypes.arrayOf(PropTypes.node),
    actionsTitle: PropTypes.string,
    pagination: PropTypes.shape({
      current: PropTypes.number,
      total: PropTypes.number,
      pageSize: PropTypes.number,
    }),
    rowKey: PropTypes.string,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    onChange: PropTypes.func,
    fetching: PropTypes.bool,
  };

  static defaultProps = {
    rowKey: 'id',
  };

  constructor(props) {
    super(props);
    this.state = { editingKey: '' };
    this.columns = [
      ...this.props.columns.map((column, index) => ({
        ...column,
        key: index,
        title: column.label,
        dataIndex: column.path,
        width: column.width,
        sorter: column.sort ? (this.props.lazy ? true : (a, b) => this.sort(a, b, column.path)) : null,
        defaultSortOrder: column.defaultSortOrder,
      })),
      {
        key: this.props.columns.length,
        title: this.props.actionsTitle ? this.props.actionsTitle : 'Actions',
        dataIndex: 'operation',
        width: props.actions ? (React.Children.count(props.actions) + 1) * 32 + 25 + 'px' : '85px', //32 represents default button width, and 25 column padding
        render: (text, record) => {
          const { editingKey } = this.state;
          const editable = this.isEditing(record);
          return editable ? (
            <span>
              <EditableContext.Consumer>
                {(form) => (
                  <Button icon="check" type="primary" buttonType="button" onClick={() => this.save(form, record)} />
                )}
              </EditableContext.Consumer>{' '}
              <Button icon="close" onClick={() => this.cancel(record[this.props.rowKey])} />
            </span>
          ) : (
            <div>
              <Button icon="edit" disabled={editingKey !== ''} onClick={() => this.edit(record[this.props.rowKey])} />
              {React.Children.map(this.props.actions, (child) =>
                child.props.onClick
                  ? [
                      ' ',
                      React.cloneElement(child, {
                        onClick: () => child.props.onClick(record),
                        disabled: editingKey !== '',
                      }),
                    ]
                  : [' ', child]
              )}
            </div>
          );
        },
      },
    ];
  }

  sort = (a, b, path) => {
    if (isString(get(a, path, ''))) {
      return get(a, path, '').localeCompare(get(b, path, ''));
    }
    return a[path] - b[path];
  };

  isEditing = (record) => record[this.props.rowKey] === this.state.editingKey;

  cancel = () => {
    this.setState({ editingKey: '' });
  };

  save(form, record) {
    form.validateFields((error, values) => {
      if (error) {
        return;
      }
      this.props.onSave(record, values);
      this.cancel();
    });
  }

  edit(key) {
    this.setState({ editingKey: key });
  }

  render() {
    const components = {
      body: {
        cell: EditableCell,
      },
    };

    const columns = this.columns.map((col) => {
      if (!col.editable) {
        return col;
      }

      const propCol = this.props.columns.find((propCol, index) => col.key === index);

      return {
        ...col,
        title: propCol.label,
        dataIndex: propCol.path,
        render: propCol.render,
        onCell: (record) => ({
          record,
          inputType: 'text', //col.dataIndex === 'age' ? 'number' : 'text',
          render: propCol.render,
          dataIndex: propCol.path,
          title: propCol.label,
          editing: this.isEditing(record),
        }),
      };
    });

    return (
      <EditableContext.Provider value={this.props.form}>
        <Table
          rowKey={this.props.rowKey}
          loading={this.props.fetching}
          components={components}
          bordered
          dataSource={this.props.rows}
          columns={columns}
          size="small"
          rowClassName="editable-row"
          pagination={this.props.pagination}
          onChange={this.props.onChange}
        />
      </EditableContext.Provider>
    );
  }
}

const EditableFormTable = Form.create()(EditableTable);

export default EditableFormTable;
